import dayjs from 'dayjs';
import ListContainer from 'Views/Components/_HumanWritten/List/ListContainer';
import { BulkBookingPreviewItem } from '../Hooks/usePreviewBulk';
import TripResultItem, { TripResultStatus } from './TripResultItem';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { genId } from 'Util/_HumanWritten/IdUtils';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';

interface BulkBookingResultsProps {
	departureTime?: Date | null;
	items: BulkBookingPreviewItem[];
	isLoading: boolean;
}

/**
 * "Vehicle bookings closed", "Vehicle and freight bookings closed" — what a trip is closed to when it
 * is still bookable for something else. Undefined when nothing is closed, and when everything is: the
 * status pill already says `Closed` in that case, so repeating the list adds nothing.
 */
const describeClosures = (
	status: TripResultStatus,
	closedFor?: string[],
	walkOnsNotOfferedOnVessel?: boolean,
): string | undefined => {
	// The pill already says Closed when nothing is bookable; listing the reasons under it repeats the
	// same fact in more words.
	if (status === 'Closed') {
		return undefined;
	}

	const sentences: string[] = [];

	if (closedFor && closedFor.length > 0) {
		// A plain English list: "Vehicle", "Vehicle and freight", "Vehicle, passenger and freight".
		// Only the first keeps its capital — the rest read as words inside the sentence, not as labels.
		const [first, ...rest] = closedFor.map((type, index) => (index === 0 ? type : type.toLowerCase()));
		const types =
			rest.length === 0 ? first : `${[first, ...rest.slice(0, -1)].join(', ')} and ${rest[rest.length - 1]}`;

		sentences.push(`${types} bookings closed`);
	}

	// Its own sentence, never folded into the list above. "Passenger bookings closed" would be untrue
	// here — the trip's passenger bookings are open, the vessel is simply not sold for walk-on fares,
	// and a staff member sent to check the trip's toggle would find it switched on.
	if (walkOnsNotOfferedOnVessel) {
		sentences.push("This vessel doesn't offer walk-on bookings");
	}

	return sentences.length > 0 ? sentences.join(' · ') : undefined;
};

export default function BulkBookingResults({ departureTime, items, isLoading }: BulkBookingResultsProps) {
	const foundCount = items.filter(r => r.status !== 'NoTripFound').length;
	const totalCount = items.length;

	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center h-full text-gray-400">
				<LottieSpinner />
			</div>
		);
	}

	if (totalCount === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-full text-gray-400">
				<div className="text-lg">No trips found</div>
			</div>
		);
	}

	return (
		<ListContainer
			title={`Search Results ${departureTime ? `(${formatStandardTime(departureTime)})` : ''}`}
			rightContent={
				<span className="text-sm font-medium text-gray-500">
					Found {foundCount} of {totalCount} potential trips
				</span>
			}
		>
			{items.map(result => (
				<TripResultItem
					key={result.tripId ?? genId()}
					date={new Date(result.date)}
					status={result.status}
					time={result.time}
					note={describeClosures(result.status, result.closedFor, result.walkOnsNotOfferedOnVessel)}
				/>
			))}
		</ListContainer>
	);
}
